import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { MdArrowBack, MdClose, MdSearch } from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import { FONT } from './contentShared';

export const YELLOW_BTN = {
  fontFamily: FONT,
  textTransform: 'none',
  background: 'rgb(255 209 2)',
  color: 'black',
  px: 3,
  py: 1,
  borderRadius: '10px',
  minWidth: 150,
  fontWeight: 800,
  boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)',
  '&:hover': { background: 'rgb(255 190 0)' },
  '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8' },
};
export const GHOST_BTN = {
  fontFamily: FONT,
  textTransform: 'none',
  fontWeight: 700,
  px: 3,
  py: 1,
  borderRadius: '10px',
  minWidth: 110,
  color: '#475569',
  borderColor: '#cbd5e1',
  '&:hover': { borderColor: '#94a3b8', backgroundColor: '#f1f5f9' },
};

export const STATUS_OPTIONS = [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }];
export const AUDIENCE_OPTIONS = [{ value: 'all', label: 'Everyone' }, { value: 'b2b', label: 'B2B customers' }, { value: 'b2c', label: 'B2C customers' }];

// A bordered card with a coloured top edge, an icon and a hint line.
export const Section = ({ title, hint, accent = '#6366f1', icon: Icon, children }) => (
  <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}` }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      {Icon && <Box sx={{ background: `${accent}1a`, color: accent, borderRadius: '10px', p: 1, display: 'flex' }}><Icon size={18} /></Box>}
      <Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14, color: '#0f172a', lineHeight: 1.2 }}>{title}</Typography>
        {hint && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{hint}</Typography>}
      </Box>
    </Box>
    {children}
  </Paper>
);

// Title row with a back arrow, used by the two inline (full-width) forms.
export const FormHeader = ({ title, subtitle, onBack }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
    <IconButton onClick={onBack} aria-label="back" sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
      <MdArrowBack size={20} />
    </IconButton>
    <Box>
      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 23.5, color: '#0f172a', lineHeight: 1.2 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{subtitle}</Typography>}
    </Box>
  </Box>
);

// Small text / select field with the shared look.
export const Field = ({ options, children, ...props }) => (
  <TextField fullWidth size="small" select={!!options} {...props}>
    {options && options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
    {children}
  </TextField>
);

// Image drop area with a live preview. `src` is the current preview URL.
export const ImageUpload = ({ src, onFile, label, error, aspect = '16 / 7' }) => (
  <Box>
    <Box
      component="label"
      sx={{
        display: 'block', position: 'relative', cursor: 'pointer', borderRadius: '14px', overflow: 'hidden',
        border: `2px dashed ${error ? '#ef4444' : '#cbd5e1'}`, background: '#f8fafc', aspectRatio: aspect,
        '&:hover': { borderColor: 'rgb(255 190 0)', background: 'rgb(255 209 2 / 6%)' },
      }}
    >
      <input type="file" accept="image/*" hidden onChange={(e) => onFile(e.target.files?.[0] || null)} />
      {src ? (
        <img src={src} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      ) : (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, color: '#334155' }}>{label}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>Click to choose an image</Typography>
        </Box>
      )}
      {src && (
        <Box sx={{ position: 'absolute', right: 10, bottom: 10, background: 'rgba(15,23,42,.75)', color: '#fff', borderRadius: '8px', px: 1.25, py: 0.5, fontFamily: FONT, fontSize: 11, fontWeight: 700 }}>
          Change image
        </Box>
      )}
    </Box>
    {error && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#dc2626', mt: 0.5 }}>{error}</Typography>}
  </Box>
);

// Search / filter / tick products. Used by the layout and tab forms.
// `known` lets edit mode show names for products the list has not loaded yet.
export const ProductPicker = ({ selectedIds, onChange, known = [] }) => {
  const [products, setProducts] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState(['All']);
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const raw = sessionStorage.getItem('token');
    const token = raw ? `Bearer ${raw}` : '';
    let cancelled = false;
    getData('categories/list?limit=1000', null, token)
      .then((res) => { if (!cancelled && res && Array.isArray(res.categories)) setCategoryOptions(['All', ...res.categories.map((c) => c.title)]); })
      .catch(() => {});
    getData('product/list?isFree=false&limit=1000', null, token)
      .then((res) => {
        if (cancelled) return;
        setProducts(Array.isArray(res?.products) ? res.products.map((p) => ({
          id: p._id,
          name: p.title,
          category: p.category_id?.title || 'N/A',
          image: p.product_images && p.product_images.length > 0 ? p.product_images[0] : null,
        })) : []);
      })
      .catch(() => { if (!cancelled) setProducts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const byId = useMemo(() => {
    const map = new Map();
    known.forEach((k) => { const id = k._id || k.id; if (id) map.set(id, { id, name: k.name || k.title, image: k.image }); });
    products.forEach((p) => map.set(p.id, p));
    return map;
  }, [products, known]);

  const visible = products.filter((p) => {
    const q = search.trim().toLowerCase();
    const okSearch = !q || p.name?.toLowerCase().includes(q) || p.id.toLowerCase().includes(q);
    return okSearch && (category === 'All' || p.category === category);
  });
  const selected = new Set(selectedIds);
  const toggle = (id) => onChange(selected.has(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  const addVisible = () => onChange([...new Set([...selectedIds, ...visible.map((p) => p.id)])]);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <TextField
          size="small" placeholder="Search products" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 200 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} color="#94a3b8" /></InputAdornment> }}
        />
        <TextField select size="small" label="Category" value={category} onChange={(e) => setCategory(e.target.value)} sx={{ minWidth: 190 }}>
          {categoryOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#0f172a' }}>
          {selectedIds.length} selected
          <Typography component="span" sx={{ fontFamily: FONT, fontWeight: 600, fontSize: 11.5, color: '#64748b' }}> · {visible.length} shown</Typography>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={addVisible} disabled={!visible.length} sx={{ textTransform: 'none', fontWeight: 700 }}>Select all shown</Button>
          <Button size="small" onClick={() => onChange([])} disabled={!selectedIds.length} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>
        </Box>
      </Box>

      {selectedIds.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5, maxHeight: 110, overflow: 'auto' }}>
          {selectedIds.map((id) => {
            const p = byId.get(id);
            return (
              <Chip
                key={id}
                avatar={<Avatar src={p?.image || undefined} />}
                label={p?.name || 'Unknown product'}
                onDelete={() => toggle(id)}
                deleteIcon={<MdClose />}
                sx={{ fontFamily: FONT, fontWeight: 600, background: '#f1f5f9', maxWidth: 260 }}
              />
            );
          })}
        </Box>
      )}

      <Paper elevation={0} sx={{ maxHeight: 340, overflow: 'auto', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
        {loading ? (
          <Typography sx={{ p: 2, fontFamily: FONT, color: '#64748b' }}>Loading products…</Typography>
        ) : visible.length === 0 ? (
          <Typography sx={{ p: 2, fontFamily: FONT, color: '#64748b' }}>No products found.</Typography>
        ) : (
          visible.map((p) => (
            <Box
              key={p.id}
              onClick={() => toggle(p.id)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75, cursor: 'pointer', borderBottom: '1px solid #f1f5f9', background: selected.has(p.id) ? 'rgb(255 209 2 / 10%)' : 'transparent', '&:hover': { background: selected.has(p.id) ? 'rgb(255 209 2 / 18%)' : '#f8fafc' } }}
            >
              <Checkbox checked={selected.has(p.id)} tabIndex={-1} disableRipple sx={{ p: 0.5, color: 'rgb(255 190 0)', '&.Mui-checked': { color: 'rgb(255 190 0)' } }} />
              <Avatar variant="rounded" src={p.image || undefined} sx={{ width: 38, height: 38, background: '#f1f5f9' }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 12.5, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#64748b' }}>{p.category}</Typography>
              </Box>
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
};
